import { Component, inject, OnInit, OnDestroy, signal, computed, effect } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { Observable, Subject, finalize, map, takeUntil } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import {
  DynamicFilterConfig,
  DynamicFilterValues,
  EnumValues,
  FilterOption,
  Note,
  UserLite,
  createDynamicFilterState,
} from '@card-software/library';
import { AuthService, SiteService } from '@card-software/nexus-utilities';
import { PaginatorState } from 'primeng/paginator';
import { MenuItem } from 'primeng/api';
import { EnumService } from '../../../services/enum.service';
import {
  LotItemTransferDto,
  LotItemTransferInsertCommand,
  LotItemTransferSearchCommand,
  LotItemTransferStatus,
  LotItemTransferType,
} from '../../../models/inventory/lot-item-transfer';
import { Batch } from '../../../models/production/production-work-order';
import { UserGroup } from '../../../models/authorization/enum/user-group.enum';
import { LotItemTransferProxyService } from '../../../proxies/inventory/lot-item-transfer.proxy.service';
import { BatchProxyService } from '../../../proxies/production/batch.proxy.service';
import { UserAuthenticationService } from '../../../services/user-authentication.service';
import { AutoCompleteSelectEvent } from 'primeng/autocomplete';

interface LotItemTransferFilters {
  searchByNumber?: string;
  status?: (LotItemTransferStatus | { value: LotItemTransferStatus })[] | null;
}

@Component({
  selector: 'app-lot-item-transfers',
  templateUrl: './lot-item-transfers.component.html',
  styleUrl: './lot-item-transfers.component.scss',
})
export class LotItemTransfersComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private enumService = inject(EnumService);
  private translateService = inject(TranslateService);
  private readonly lotItemTransferProxyService = inject(LotItemTransferProxyService);
  private readonly batchProxyService = inject(BatchProxyService);
  private readonly userAuthService = inject(UserAuthenticationService);

  private destroy$ = new Subject<void>();
  private appliedFilters: LotItemTransferFilters = {};
  isLoading = signal<number>(0);
  lotItemTransfers = signal<LotItemTransferDto[]>([]);
  showFilters = signal<boolean>(true);
  filterConfig = signal<DynamicFilterConfig | null>(null);

  lotItemTransferStatus = signal<EnumValues>({});
  private readonly filterConfigWithOptions = computed(() =>
    Object.keys(this.lotItemTransferStatus()).length > 0 ? this.filterConfig() : null,
  );
  readonly filterState = createDynamicFilterState({ storageKey: 'lot-item-transfers-filters', config: this.filterConfigWithOptions });
  lotItemTransferType = signal<EnumValues>({});

  // Transfer type enum for template
  readonly LotItemTransferType = LotItemTransferType;

  // Site IDs that drive which transfer types are shown.
  // Why: ChemicalBatchToFinishedGoods only makes sense for the Pride Chemicals site; greige and
  // relocation transfers are only enabled for the Northern Textiles site.
  private static readonly SITE_ID_PRIDE_CHEMICALS = '698d239b9742c79b2f8cb1f2';
  private static readonly SITE_ID_NORTHERN_TEXTILES = '698d23639742c79b2f8cb1f1';

  // Captured at construction; site switches reload the app, so a static snapshot is sufficient.
  private readonly currentSiteId = signal<string | null>(SiteService.getSelectedSiteId());

  // SpeedDial items for creating new transfers
  speedDialItems = computed<MenuItem[]>(() => {
    const siteId = this.currentSiteId();
    const items: MenuItem[] = [];

    // Sample senders reach their Greige/Finished sample options through this same dialog,
    // so they must be able to open it even without the generic editor roles.
    if (
      AuthService.hasAnyRole([
        UserGroup.LotItemTransferEditor,
        UserGroup.LotItemTransferAdmin,
        UserGroup.GreigeSampleSender,
        UserGroup.FinishedSampleSender,
      ])
    ) {
      items.push({
        label: 'Standard',
        icon: 'pi pi-arrow-right',
        tooltip: 'New Standard Transfer',
        command: () => this.showStandardTransferDialog(),
      });
    }

    // Relocation hidden for Pride Chemicals; shown only for users with relocation admin roles.
    const canDoFGRelocation = AuthService.hasRole(UserGroup.FinishedGoodsRelocationAdmin);
    const canDoRMRelocation = AuthService.hasRole(UserGroup.RawMaterialRelocationAdmin);
    if (siteId !== LotItemTransfersComponent.SITE_ID_PRIDE_CHEMICALS && (canDoFGRelocation || canDoRMRelocation)) {
      items.push({
        label: 'Relocation',
        icon: 'pi pi-arrows-alt',
        tooltip: 'New Relocation Transfer',
        command: () => this.showRelocationDialog.set(true),
      });
    }

    return items;
  });

  // Standard transfer selection dialog
  showStandardDialog = signal<boolean>(false);

  // Relocation warehouse selection dialog
  showRelocationDialog = signal<boolean>(false);

  // Options shown inside the Relocation dialog, gated by role
  relocationTypeOptions = computed(() => {
    const options: {
      id: string;
      warehouse: 'WH-FG-1' | 'WH-RM-1';
      label: string;
      description: string;
      fromLabel: string;
      toLabel: string;
      icon: string;
    }[] = [];
    if (AuthService.hasRole(UserGroup.FinishedGoodsRelocationAdmin)) {
      options.push({
        id: 'fg-relocation',
        warehouse: 'WH-FG-1',
        label: 'Finished Goods Relocation',
        description: 'Relocate rolls within the Finished Goods warehouse',
        fromLabel: 'NT - Finished Goods',
        toLabel: 'NT - Finished Goods',
        icon: 'pi pi-box',
      });
    }
    if (AuthService.hasRole(UserGroup.RawMaterialRelocationAdmin)) {
      options.push({
        id: 'rm-relocation',
        warehouse: 'WH-RM-1',
        label: 'Raw Material Relocation',
        description: 'Relocate yarn lot items within the Raw Material warehouse',
        fromLabel: 'NT - Raw Material',
        toLabel: 'NT - Raw Material',
        icon: 'pi pi-box',
      });
    }
    return options;
  });

  // Batch selection dialog (for TransferToFinishedGoods)
  showBatchSelectionDialog = signal<boolean>(false);
  pendingTransferOption = signal<(typeof this.allStandardTransferOptions)[number] | null>(null);
  selectedBatchForTransfer = signal<Batch | null>(null);

  // Sub-location selection dialog (for sample transfers)
  showSubLocationDialog = signal<boolean>(false);
  pendingSampleOption = signal<(typeof this.allStandardTransferOptions)[number] | null>(null);

  // Notes dialog (third modal — after sub-location selection)
  showNotesDialog = signal<boolean>(false);
  pendingSubLocation = signal<{ code: string; label: string } | null>(null);
  pendingNotes = signal<Note[]>([]);
  userLite: UserLite | null = null;

  private static readonly SAMPLE_SUB_LOCATIONS = [
    { code: 'NT.WH-SM-1.ZN-PDSM-1', label: 'Product Development Samples' },
    { code: 'NT.WH-SM-1.ZN-DYSM-1', label: 'Dyeing Lab Samples' },
    { code: 'NT.WH-SM-1.ZN-QCSM-1', label: 'QC Lab Samples' },
    { code: 'NT.WH-SM-1.ZN-CLSM-1', label: 'Client Samples' },
  ];
  readonly sampleSubLocations = LotItemTransfersComponent.SAMPLE_SUB_LOCATIONS;

  // Full list of standard transfer options (filtered by current site in `standardTransferOptions`).
  private readonly allStandardTransferOptions = [
    {
      id: 'finished-goods-to-rolling',
      type: LotItemTransferType.FinishedGoodsToRolling,
      label: 'Producto Terminado a Enrollado',
      description: 'Transfer rolls from Finished Goods to Rolling warehouse',
      fromLocationCode: 'NT.WH-FG-1',
      toLocationCode: 'NT.WH-RG-1',
      fromLabel: 'NT - Finished Goods',
      toLabel: 'NT - Rolling',
      icon: 'pi pi-arrow-right',
      siteId: LotItemTransfersComponent.SITE_ID_NORTHERN_TEXTILES,
    },
    {
      id: 'finished-goods-nt-to-py',
      type: LotItemTransferType.FinishedGoodsTransfer,
      label: 'Transferencia NT → PY',
      description: 'Transfer rolls from NT Finished Goods to PY Finished Goods',
      fromLocationCode: 'NT.WH-FG-1',
      toLocationCode: 'PY.WH-FG-1',
      fromLabel: 'NT - Finished Goods',
      toLabel: 'PY - Finished Goods',
      icon: 'pi pi-arrow-right',
      siteId: LotItemTransfersComponent.SITE_ID_NORTHERN_TEXTILES,
    },
    {
      id: 'finished-goods-py-to-nt',
      type: LotItemTransferType.FinishedGoodsTransfer,
      label: 'Transferencia PY → NT',
      description: 'Transfer rolls from PY Finished Goods to NT Finished Goods',
      fromLocationCode: 'PY.WH-FG-1',
      toLocationCode: 'NT.WH-FG-1',
      fromLabel: 'PY - Finished Goods',
      toLabel: 'NT - Finished Goods',
      icon: 'pi pi-arrow-left',
      siteId: LotItemTransfersComponent.SITE_ID_NORTHERN_TEXTILES,
    },
    {
      id: 'transfer-to-finished-goods',
      type: LotItemTransferType.ChemicalBatchToFinishedGoods,
      label: 'Chemical Transferencia a Producto Terminado',
      description: 'Transfer lot items from Production to Finished Goods warehouse',
      fromLocationCode: 'PC.WH-PROD-1',
      toLocationCode: 'PC.WH-FG-1',
      fromLabel: 'PC - Production',
      toLabel: 'PC - Finished Goods',
      icon: 'pi pi-box',
      siteId: LotItemTransfersComponent.SITE_ID_PRIDE_CHEMICALS,
    },
    {
      id: 'raw-material-to-finished-goods-nt',
      type: LotItemTransferType.RawMaterialToFinishedGoods,
      label: 'Materia Prima a Producto Terminado',
      description: 'Transfer lot items from Raw Materials to Finished Goods',
      fromLocationCode: 'NT.WH-RM-1',
      toLocationCode: 'NT.WH-FG-1',
      fromLabel: 'NT - Materia Prima',
      toLabel: 'NT - Producto Terminado',
      icon: 'pi pi-box',
      siteId: LotItemTransfersComponent.SITE_ID_NORTHERN_TEXTILES,
    },
    {
      id: 'raw-material-to-finished-goods-pc',
      type: LotItemTransferType.RawMaterialToFinishedGoods,
      label: 'Materia Prima a Producto Terminado',
      description: 'Transfer lot items from Raw Materials to Finished Goods',
      fromLocationCode: 'PC.WH-RM-1',
      toLocationCode: 'PC.WH-FG-1',
      fromLabel: 'PC - Materia Prima',
      toLabel: 'PC - Producto Terminado',
      icon: 'pi pi-box',
      siteId: LotItemTransfersComponent.SITE_ID_PRIDE_CHEMICALS,
    },
    {
      id: 'finished-goods-to-raw-material-pc',
      type: LotItemTransferType.FinishedGoodsToRawMaterial,
      label: 'Producto Terminado a Materia Prima',
      description: 'Transfer lot items from Finished Goods to Raw Materials',
      fromLocationCode: 'PC.WH-FG-1',
      toLocationCode: 'PC.WH-RM-1',
      fromLabel: 'PC - Producto Terminado',
      toLabel: 'PC - Materia Prima',
      icon: 'pi pi-box',
      siteId: LotItemTransfersComponent.SITE_ID_PRIDE_CHEMICALS,
    },
    {
      id: 'greige-transfer',
      type: LotItemTransferType.TransferToFinishedGoods,
      label: 'Greige a Producto Terminado',
      description: 'Transfer greige rolls from Greige Warehouse to Finished Goods',
      fromLocationCode: 'NT.WH-GR-1',
      toLocationCode: 'NT.WH-FG-1',
      fromLabel: 'NT - Greige',
      toLabel: 'NT - Producto Terminado',
      icon: 'pi pi-box',
      siteId: LotItemTransfersComponent.SITE_ID_NORTHERN_TEXTILES,
    },
    {
      id: 'greige-sample',
      type: LotItemTransferType.GreigeSample,
      label: 'Muestra de Greige',
      description: 'Transfer greige sample rolls to Samples warehouse',
      fromLocationCode: 'NT.WH-GR-1',
      toLocationCode: 'NT.WH-SM-1',
      fromLabel: 'NT - Greige',
      toLabel: 'NT - Muestras',
      icon: 'pi pi-tag',
      siteId: LotItemTransfersComponent.SITE_ID_NORTHERN_TEXTILES,
    },
    {
      id: 'finished-sample-finishing',
      type: LotItemTransferType.FinishedSample,
      label: 'Muestra de Acabado (Finishing)',
      description: 'Transfer finished sample rolls from Finishing to Samples warehouse',
      fromLocationCode: 'NT.WH-FIN-1',
      toLocationCode: 'NT.WH-SM-1',
      fromLabel: 'NT - Finishing',
      toLabel: 'NT - Muestras',
      icon: 'pi pi-tag',
      siteId: LotItemTransfersComponent.SITE_ID_NORTHERN_TEXTILES,
    },
    {
      id: 'finished-sample-rolling',
      type: LotItemTransferType.FinishedSample,
      label: 'Muestra de Acabado (Rolling)',
      description: 'Transfer finished sample rolls from Rolling to Samples warehouse',
      fromLocationCode: 'NT.WH-RG-1',
      toLocationCode: 'NT.WH-SM-1',
      fromLabel: 'NT - Rolling',
      toLabel: 'NT - Muestras',
      icon: 'pi pi-tag',
      siteId: LotItemTransfersComponent.SITE_ID_NORTHERN_TEXTILES,
    },
    {
      id: 'finished-sample-finished-goods',
      type: LotItemTransferType.FinishedSample,
      label: 'Muestra de Acabado (PT)',
      description: 'Transfer finished sample rolls from Finished Goods to Samples warehouse',
      fromLocationCode: 'NT.WH-FG-1',
      toLocationCode: 'NT.WH-SM-1',
      fromLabel: 'NT - Producto Terminado',
      toLabel: 'NT - Muestras',
      icon: 'pi pi-tag',
      siteId: LotItemTransfersComponent.SITE_ID_NORTHERN_TEXTILES,
    },
  ];

  // Strict per-site filtering: each option declares the site it belongs to, and only options
  // matching the current site are shown. An unknown/null site shows nothing (future sites must
  // tag their options explicitly before they appear).
  standardTransferOptions = computed(() => {
    const siteId = this.currentSiteId();
    const isAdmin = AuthService.hasRole(UserGroup.LotItemTransferAdmin);
    const isGreigeSender = AuthService.hasRole(UserGroup.GreigeSampleSender);
    const isFinishedSender = AuthService.hasRole(UserGroup.FinishedSampleSender);

    return this.allStandardTransferOptions.filter((o) => {
      if (o.siteId !== siteId) return false;
      if (o.type === LotItemTransferType.GreigeSample) return isAdmin || isGreigeSender;
      if (o.type === LotItemTransferType.FinishedSample) return isAdmin || isFinishedSender;
      return true;
    });
  });

  // Static status options (fallback if enum service doesn't have them)
  statusOptions = computed<FilterOption[]>(() => {
    const statuses = this.lotItemTransferStatus();
    if (Object.keys(statuses).length > 0) {
      return Object.keys(statuses)
        .filter((key) => !isNaN(Number(key)))
        .map((key) => ({
          value: Number(key),
          label: statuses[Number(key)],
        }));
    }
    // Fallback to hardcoded values if enum not available
    return [
      { value: LotItemTransferStatus.Draft, label: 'Draft' },
      { value: LotItemTransferStatus.ReadyForTransfer, label: 'Ready For Transfer' },
      { value: LotItemTransferStatus.InRoute, label: 'In Route' },
      { value: LotItemTransferStatus.Received, label: 'Received' },
      { value: LotItemTransferStatus.Voided, label: 'Voided' },
    ];
  });

  // Pagination
  currentPage = 1;
  pageSize = 25;
  totalRecords = 0;

  canEdit = computed(() => {
    return AuthService.hasAnyRole([
      UserGroup.LotItemTransferEditor,
      UserGroup.LotItemTransferAdmin,
      UserGroup.FinishedGoodsRelocationAdmin,
      UserGroup.RawMaterialRelocationAdmin,
      UserGroup.GreigeSampleSender,
      UserGroup.FinishedSampleSender,
    ]);
  });

  getRowData(rowData: unknown): LotItemTransferDto {
    return rowData as LotItemTransferDto;
  }

  constructor() {
    this.filterState.applied$.pipe(takeUntilDestroyed()).subscribe((filters) => this.searchWithFilters(filters));

    effect(
      () => {
        const statusesValue = this.enumService.getEnum('LotItemTransferStatus', 'inventory').values;
        if (Object.keys(statusesValue).length > 0) {
          this.lotItemTransferStatus.set(statusesValue);
        }
      },
      { allowSignalWrites: true },
    );

    effect(
      () => {
        const typesValue = this.enumService.getEnum('LotItemTransferType', 'inventory').values;
        if (Object.keys(typesValue).length > 0) {
          this.lotItemTransferType.set(typesValue);
        }
      },
      { allowSignalWrites: true },
    );

    effect(
      () => {
        if (this.translateService.currentLang) {
          this.tryInitializeFilterConfig();
        }
      },
      { allowSignalWrites: true },
    );
  }

  ngOnInit(): void {
    this.userLite = this.userAuthService.userLite;
    this.translateService.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.tryInitializeFilterConfig();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getTransferStrict(transfer: unknown): LotItemTransferDto {
    return transfer as LotItemTransferDto;
  }

  private tryInitializeFilterConfig(): void {
    if (this.translateService.currentLang) {
      this.initializeFilterConfig();
    }
  }

  private initializeFilterConfig(): void {
    const translate = (key: string, fallback: string) => {
      const translation = this.translateService.instant(key);
      return translation === key ? fallback : translation;
    };

    const config: DynamicFilterConfig = {
      persistInUrl: true,
      persistVisibility: true,
      filters: [
        {
          type: 'text',
          formControlName: 'searchByNumber',
          label: translate('Lot_Item_Transfer_Number', 'Transfer Number'),
          placeholder: translate('Enter_transfer_number', 'Enter transfer number'),
          defaultVisible: true,
        },
        {
          type: 'multiselect',
          formControlName: 'status',
          label: translate('Status', 'Status'),
          placeholder: translate('Select_status', 'Select status'),
          options: this.statusOptions(),
          showClear: true,
          defaultVisible: true,
        },
      ],
    };

    this.filterConfig.set(config);
  }

  private startLoading(): void {
    this.isLoading.update((val) => val + 1);
  }

  private stopLoading(): void {
    this.isLoading.update((val) => val - 1);
  }

  search() {
    const formValues = this.appliedFilters;
    const searchTerm = formValues.searchByNumber?.trim();
    const statusesRaw = formValues.status;
    const statuses = Array.isArray(statusesRaw)
      ? statusesRaw.map((s: LotItemTransferStatus | { value: LotItemTransferStatus }): LotItemTransferStatus =>
          typeof s === 'object' && s.value !== undefined ? s.value : (s as LotItemTransferStatus),
        )
      : [];

    const command: LotItemTransferSearchCommand = {
      statuses: statuses && statuses.length > 0 ? statuses : undefined,
      searchTerm: searchTerm || undefined,
    };

    this.startLoading();
    this.lotItemTransferProxyService
      .search(command, this.currentPage, this.pageSize)
      .pipe(
        finalize(() => {
          this.stopLoading();
        }),
      )
      .subscribe({
        next: (result) => {
          this.lotItemTransfers.set(result?.data?.items || []);
          this.totalRecords = result?.data?.totalCount || 0;
        },
        error: () => {
          this.lotItemTransfers.set([]);
          this.totalRecords = 0;
        },
      });
  }

  onPageChange(event: PaginatorState) {
    this.currentPage = (event.page || 0) + 1;
    this.pageSize = event.rows || this.pageSize;
    this.search();
  }

  private searchWithFilters(filters: DynamicFilterValues): void {
    this.appliedFilters = filters as LotItemTransferFilters;
    this.currentPage = 1;
    this.search();
  }

  redirectToDetails(id?: string): void {
    const targetId = id ?? 'new';
    this.router.navigate(['lot-item-transfer-details'], { queryParams: { id: targetId } });
  }

  showStandardTransferDialog(): void {
    this.showStandardDialog.set(true);
  }

  hideStandardTransferDialog(): void {
    this.showStandardDialog.set(false);
  }

  selectStandardTransferOption(option: (typeof this.allStandardTransferOptions)[number]): void {
    this.hideStandardTransferDialog();

    if (option.type === LotItemTransferType.GreigeSample || option.type === LotItemTransferType.FinishedSample) {
      this.pendingSampleOption.set(option);
      this.showSubLocationDialog.set(true);
      return;
    }

    this.createTransferFromOption(option);
  }

  selectSubLocation(subLocation: { code: string; label: string }): void {
    const option = this.pendingSampleOption();
    if (!option) return;

    this.showSubLocationDialog.set(false);
    this.pendingSubLocation.set(subLocation);
    this.pendingNotes.set([]);
    this.showNotesDialog.set(true);
  }

  cancelSubLocationSelection(): void {
    this.showSubLocationDialog.set(false);
    this.pendingSampleOption.set(null);
  }

  onSampleNoteAdded(note: Note): void {
    this.pendingNotes.update((notes) => [...notes, note]);
  }

  confirmNotesAndCreateTransfer(): void {
    const option = this.pendingSampleOption();
    const subLocation = this.pendingSubLocation();
    const notes = this.pendingNotes();
    if (!option || !subLocation || notes.length === 0) return;

    this.showNotesDialog.set(false);
    this.pendingSampleOption.set(null);
    this.pendingSubLocation.set(null);
    this.pendingNotes.set([]);
    this.createTransferFromOption({ ...option, toLocationCode: subLocation.code }, undefined, notes);
  }

  cancelNotesDialog(): void {
    this.showNotesDialog.set(false);
    this.pendingSampleOption.set(null);
    this.pendingSubLocation.set(null);
    this.pendingNotes.set([]);
  }

  createTransferFromOption(
    option: (typeof this.allStandardTransferOptions)[number],
    initiator?: { number: string; entityDefinitionCode: number; reference: string },
    notes?: Note[],
  ): void {
    const command: LotItemTransferInsertCommand = {
      fromLocationCode: option.fromLocationCode,
      toLocationCode: option.toLocationCode,
      transferType: option.type,
      ...(initiator ? { initiator } : {}),
      ...(notes ? { notes } : {}),
    };

    this.startLoading();
    this.lotItemTransferProxyService
      .create(command)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => {
          if (response.success && response.data) {
            this.router.navigate(['lot-item-transfer-details'], {
              queryParams: { id: response.data.id },
            });
          }
        },
      });
  }

  selectRelocationOption(warehouse: 'WH-FG-1' | 'WH-RM-1'): void {
    this.showRelocationDialog.set(false);
    this.createRelocationTransferWithWarehouse(warehouse);
  }

  createRelocationTransferWithWarehouse(warehouse: 'WH-FG-1' | 'WH-RM-1'): void {
    const locationCode = warehouse === 'WH-RM-1' ? 'NT.WH-RM-1' : 'NT.WH-FG-1';
    const command: LotItemTransferInsertCommand = {
      fromLocationCode: locationCode,
      toLocationCode: locationCode,
      transferType: LotItemTransferType.Relocation,
    };

    this.startLoading();
    this.lotItemTransferProxyService
      .create(command)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => {
          if (response.success && response.data) {
            this.router.navigate(['lot-item-transfer-details'], {
              queryParams: { id: response.data.id },
            });
          }
        },
      });
  }

  // Batch selection for TransferToFinishedGoods
  public fetchBatches = (query: string): Observable<Batch[]> =>
    this.batchProxyService.search({ number: query }, 1, 20).pipe(map((response) => response?.data?.items || []));

  onBatchSelect(event: AutoCompleteSelectEvent): void {
    this.selectedBatchForTransfer.set(event.value as Batch);
  }

  confirmBatchAndCreateTransfer(): void {
    const option = this.pendingTransferOption();
    const batch = this.selectedBatchForTransfer();
    if (!option || !batch) return;

    this.showBatchSelectionDialog.set(false);
    this.createTransferFromOption(option, {
      number: batch.number,
      entityDefinitionCode: 41, // Batch
      reference: batch.id,
    });

    this.pendingTransferOption.set(null);
    this.selectedBatchForTransfer.set(null);
  }

  cancelBatchSelection(): void {
    this.showBatchSelectionDialog.set(false);
    this.pendingTransferOption.set(null);
    this.selectedBatchForTransfer.set(null);
  }

  getItemsCount(transfer: LotItemTransferDto): number {
    return transfer.items?.length || 0;
  }

  getStatusLabel(status: number): string {
    const statusEnum = this.lotItemTransferStatus();
    return statusEnum[status] || status.toString();
  }

  getTransferTypeLabel(type: number): string {
    const typeEnum = this.lotItemTransferType();
    if (typeEnum[type]) {
      return typeEnum[type];
    }
    // Fallback labels if enum not loaded
    switch (type) {
      case LotItemTransferType.FinishedGoodsToRolling:
        return 'PT a Enrollado';
      case LotItemTransferType.Relocation:
        return 'Reubicación';
      case LotItemTransferType.FinishedGoodsTransfer:
        return 'Transferencia PT';
      case LotItemTransferType.TransferToFinishedGoods:
        return 'Transferencia a PT';
      case LotItemTransferType.RawMaterialToFinishedGoods:
        return 'Materia Prima a Producto Terminado';
      case LotItemTransferType.FinishedGoodsToRawMaterial:
        return 'Producto Terminado a Materia Prima';
      case LotItemTransferType.GreigeSample:
        return 'Muestra de Greige';
      case LotItemTransferType.FinishedSample:
        return 'Muestra de Acabado';
      default:
        return type.toString();
    }
  }

  getTransferTypeSeverity(type: number): 'info' | 'warn' | 'success' {
    switch (type) {
      case LotItemTransferType.Relocation:
        return 'info';
      case LotItemTransferType.FinishedGoodsTransfer:
        return 'success';
      case LotItemTransferType.TransferToFinishedGoods:
        return 'warn';
      case LotItemTransferType.GreigeSample:
      case LotItemTransferType.FinishedSample:
        return 'info';
      default:
        return 'warn';
    }
  }

  getTransferTypeIcon(type: number): string {
    switch (type) {
      case LotItemTransferType.Relocation:
        return 'pi pi-arrows-alt';
      case LotItemTransferType.FinishedGoodsTransfer:
        return 'pi pi-truck';
      case LotItemTransferType.TransferToFinishedGoods:
        return 'pi pi-box';
      case LotItemTransferType.GreigeSample:
      case LotItemTransferType.FinishedSample:
        return 'pi pi-tag';
      default:
        return 'pi pi-arrow-right';
    }
  }
}
